import { useState, type ComponentProps } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable, Simplify } from "type-fest";

import { ReturnDetails } from "../ReturnedForChanges/ReturnedForChanges.js";

/** How reopening went: reopened, refused because the assessment moved on, or failed otherwise. */
export type ReopenOutcome = "reopened" | "changed" | "failed";

/**
 * Why a returned assessment came back, put to its assessor over the assessment
 * until they reopen it. It never closes itself: once reopened it holds
 * Continue, and goes when its caller stops rendering it.
 */
export const ReturnedForChangesDialog = ({
  onReopen,
  ...returned
}: Simplify<
  ComponentProps<typeof ReturnDetails> & {
    onReopen: () => Promisable<ReopenOutcome>;
  }
>) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<ReopenOutcome>();

  const reopen = async () => {
    setSending(true);
    setOutcome(undefined);

    try {
      setOutcome(await onReopen());
    } catch {
      setOutcome("failed");
    } finally {
      setSending(false);
    }
  };

  return (
    // No onClose: a returned assessment cannot be worked on until it is
    // reopened, so Continue is the only way on.
    <Dialog open fullWidth maxWidth="sm" fullScreen={narrow}>
      <DialogTitle>{t("assessment.returned.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/* One Stack child, so the return's own lines are not spaced apart. */}
          <div>
            <ReturnDetails {...returned} />
          </div>
          <DialogContentText variant="body2">
            {t("assessment.returned.reopenHelp")}
          </DialogContentText>
          {outcome === "changed" || outcome === "failed" ? (
            <Alert severity="error">
              {t(`assessment.returned.${outcome}`)}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          disabled={sending || outcome === "reopened"}
          onClick={() => {
            void reopen();
          }}
        >
          {t("assessment.returned.continue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
